import { Suspense, useEffect, type ReactNode } from "react";
import { useI18n, type Locale, type MessageKey } from "../lib/i18n";
import { useSeo } from "../lib/seo";
import { lazyPage as lazy } from "../lib/lazy";
import {
  createBrowserRouter,
  type RouteObject,
  Navigate,
  Outlet,
  useLocation,
} from "react-router";
import { useMe } from "../lib/queries";
import { Loading } from "../components/ui";
import { AppShell } from "./AppShell";
import { Landing } from "../pages/Landing";
import RouteError from "../pages/RouteError";

// Code-split everything behind sign-in so the landing page stays light.
const Login = lazy(() =>
  import("../pages/Auth").then((m) => ({ default: m.Login })),
);
const Signup = lazy(() =>
  import("../pages/Auth").then((m) => ({ default: m.Signup })),
);
const Home = lazy(() => import("../pages/Home"));
const Activity = lazy(() => import("../pages/Activity"));
const Funds = lazy(() => import("../pages/Funds"));
const Pay = lazy(() => import("../pages/Pay"));
const Send = lazy(() => import("../pages/Send"));
const RequestMoney = lazy(() => import("../pages/RequestMoney"));
const PayRequestPage = lazy(() => import("../pages/PayRequestPage"));
const Circles = lazy(() => import("../pages/Circles"));
const NewCircle = lazy(() => import("../pages/NewCircle"));
const CircleDetail = lazy(() => import("../pages/CircleDetail"));
const JoinCircle = lazy(() => import("../pages/JoinCircle"));
const Campaigns = lazy(() => import("../pages/Campaigns"));
const NewCampaign = lazy(() => import("../pages/NewCampaign"));
const CampaignPage = lazy(() => import("../pages/CampaignPage"));
const Me = lazy(() => import("../pages/Me"));
const NotFound = lazy(() => import("../pages/NotFound"));
const Checkout = lazy(() => import("../pages/Checkout"));
const Developers = lazy(() => import("../pages/Developers"));
const Community = lazy(() => import("../pages/Orgs").then((m) => ({ default: m.Community })));
const NewOrg = lazy(() => import("../pages/Orgs").then((m) => ({ default: m.NewOrg })));
const OrgDetail = lazy(() => import("../pages/OrgDetail"));
const Notifications = lazy(() => import("../pages/Notifications"));
const Verify = lazy(() => import("../pages/Verify"));
const Statement = lazy(() => import("../pages/Statement"));
const Security = lazy(() => import("../pages/Security"));
const Admin = lazy(() => import("../pages/Admin"));
const Legal = lazy(() => import("../pages/Legal"));
const Plan = lazy(() => import("../pages/Plan"));
const PlanAccountNew = lazy(() => import("../pages/PlanForms").then((m) => ({ default: m.PlanAccountNew })));
const PlanItemNew = lazy(() => import("../pages/PlanForms").then((m) => ({ default: m.PlanItemNew })));
const Finances = lazy(() => import("../pages/Finances"));
const Proof = lazy(() => import("../pages/Proof"));
const PlanAccountEdit = lazy(() => import("../pages/PlanForms").then((m) => ({ default: m.PlanAccountEdit })));
const PlanItemEdit = lazy(() => import("../pages/PlanForms").then((m) => ({ default: m.PlanItemEdit })));
const PlanEntryNew = lazy(() => import("../pages/PlanForms").then((m) => ({ default: m.PlanEntryNew })));
const Card = lazy(() => import("../pages/Card"));
const Receipts = lazy(() => import("../pages/Receipts"));
const ReceiptDetail = lazy(() => import("../pages/Receipts").then((m) => ({ default: m.ReceiptDetail })));
const Insights = lazy(() => import("../pages/Insights"));
const InsightProduct = lazy(() => import("../pages/Insights").then((m) => ({ default: m.InsightProduct })));

const page = (el: ReactNode) => (
  <Suspense fallback={<Loading />}>{el}</Suspense>
);

function RequireUser() {
  const me = useMe();
  const location = useLocation();
  if (me.isPending) return <Loading />;
  if (!me.data)
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}
        replace
      />
    );
  return <Outlet />;
}

function GuestOnly() {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (me.data) return <Navigate to="/app" replace />;
  return <Outlet />;
}

/** Each screen gets its own tab title, in the reader's language ("Send money · CafonaPay"). */
const TITLES: Array<[RegExp, MessageKey]> = [
  [/^\/login/, "nav.signIn"],
  [/^\/signup/, "nav.getStarted"],
  [/^\/verify/, "verify.title"],
  [/^\/app\/activity/, "nav.activity"],
  [/^\/app\/add/, "wallet.add"],
  [/^\/app\/withdraw/, "wallet.withdraw"],
  [/^\/app\/send/, "pay.send"],
  [/^\/app\/request/, "pay.request"],
  [/^\/app\/pay/, "pay.title"],
  [/^\/app\/circles/, "circles.title"],
  [/^\/app\/campaigns/, "campaigns.title"],
  [/^\/app\/me/, "me.title"],
  [/^\/app\/plan/, "plan.title"],
  [/^\/app\/card/, "card.title"],
  [/^\/app\/receipts/, "rcpt.title"],
  [/^\/app\/finances/, "fin.title"],
  [/^\/app\/proof/, "pof.title"],
  [/^\/app\/insights/, "ins.title"],
  [/^\/app\/security/, "security.title"],
  [/^\/app\/admin/, "admin.title"],
  [/^\/app\/(orgs|community)/, "org.title"],
  [/^\/app\/notifications/, "notif.title"],
  [/^\/app\/?$/, "nav.home"],
];
function TitledRoot() {
  const { pathname } = useLocation();
  const { t, locale } = useI18n();
  const key = TITLES.find(([re]) => re.test(pathname))?.[1];
  // Public pages get full search metadata; private screens get a title and noindex.
  useSeo(pathname, locale, t, key ? t(key) : null);
  return <Outlet />;
}

/** /fr/… pages are French by URL (search engines index each language at its own address). */
function Lang({ lang, children }: { lang: Locale; children: ReactNode }) {
  const { locale, setLocale } = useI18n();
  useEffect(() => {
    if (locale !== lang) setLocale(lang);
  }, [lang, locale, setLocale]);
  return <>{children}</>;
}

export const routes: RouteObject[] = [
  {
    element: <TitledRoot />,
    errorElement: <RouteError />,
    children: [
      { path: "/", element: <Landing /> },
      { path: "/fr", element: <Lang lang="fr"><Landing /></Lang> },
      { path: "/fr/legal/terms", element: <Lang lang="fr">{page(<Legal doc="terms" />)}</Lang> },
      { path: "/fr/legal/privacy", element: <Lang lang="fr">{page(<Legal doc="privacy" />)}</Lang> },
      { path: "/fr/verify", element: <Lang lang="fr">{page(<Verify />)}</Lang> },
      {
        element: <GuestOnly />,
        children: [
          { path: "/login", element: page(<Login />) },
          { path: "/signup", element: page(<Signup />) },
        ],
      },
      // Public, shareable pages: work signed out, offer sign-in to act.
      { path: "/join/:code", element: page(<JoinCircle />) },
      { path: "/c/:slug", element: page(<CampaignPage />) },
      { path: "/pay/:id", element: page(<PayRequestPage />) },
      { path: "/checkout/:id", element: page(<Checkout />) },
      { path: "/developers", element: page(<Developers />) },
      { path: "/verify", element: page(<Verify />) },
      { path: "/verify/:id", element: page(<Verify />) },
      { path: "/c/:slug/certificate", element: page(<Statement kind="campaign" />) },
      { path: "/legal/terms", element: page(<Legal doc="terms" />) },
      { path: "/legal/privacy", element: page(<Legal doc="privacy" />) },
      {
        element: <RequireUser />,
        children: [
          {
            path: "/app",
            element: <AppShell />,
            children: [
              { index: true, element: page(<Home />) },
              { path: "activity", element: page(<Activity />) },
              { path: "add", element: page(<Funds mode="add" />) },
              { path: "withdraw", element: page(<Funds mode="withdraw" />) },
              { path: "pay", element: page(<Pay />) },
              { path: "send", element: page(<Send />) },
              { path: "request", element: page(<RequestMoney />) },
              { path: "circles", element: page(<Circles />) },
              { path: "circles/new", element: page(<NewCircle />) },
              { path: "circles/:id", element: page(<CircleDetail />) },
              { path: "circles/:id/certificate", element: page(<Statement kind="circle" />) },
              { path: "campaigns", element: page(<Campaigns />) },
              { path: "campaigns/new", element: page(<NewCampaign />) },
              { path: "me", element: page(<Me />) },
              { path: "plan", element: page(<Plan />) },
              { path: "plan/accounts/new", element: page(<PlanAccountNew />) },
              { path: "plan/accounts/:id", element: page(<PlanAccountEdit />) },
              { path: "plan/items/:id", element: page(<PlanItemEdit />) },
              { path: "plan/items/new", element: page(<PlanItemNew />) },
              { path: "plan/entries/new", element: page(<PlanEntryNew />) },
              { path: "card", element: page(<Card />) },
              { path: "finances", element: page(<Finances />) },
              { path: "proof", element: page(<Proof />) },
              { path: "receipts", element: page(<Receipts />) },
              { path: "receipts/:id", element: page(<ReceiptDetail />) },
              { path: "insights", element: page(<Insights />) },
              { path: "insights/product/:product", element: page(<InsightProduct />) },
              { path: "security", element: page(<Security />) },
              { path: "admin", element: page(<Admin />) },
              { path: "admin/:tab", element: page(<Admin />) },
              { path: "admin/:tab/:slug", element: page(<Admin />) },
              { path: "community", element: page(<Community />) },
              { path: "orgs/new", element: page(<NewOrg />) },
              { path: "orgs/:slug", element: page(<OrgDetail />) },
              { path: "notifications", element: page(<Notifications />) },
            ],
          },
        ],
      },
      { path: "*", element: page(<NotFound />) },
    ],
  },
];

/** The browser router (the build-time pre-render uses `routes` with a static router instead). */
export const router = typeof window === "undefined" ? (null as unknown as ReturnType<typeof createBrowserRouter>) : createBrowserRouter(routes);
